import { Box, Typography } from '@material-ui/core'
import { capitalize } from 'lodash'
import {
  CheckboxGroupInput,
  Datagrid,
  Filter,
  FunctionField,
  TextField,
  TextInput,
  useRedirect,
} from 'react-admin'
import { TrackingStatus } from 'shared/models/load'
import { error, errorD1, kellyGreenD1, marigoldOrange, paleyellow, success } from 'shared/theme/colors'

import { getLocalizedStopAppointmentWindow } from 'helpers/loads'
import { JobTrackingDashboardStatus } from 'models/jobs'
import { LABEL_ORIGIN_TO_DESTINATION } from '../../helpers/constants'
import { getFormattedDate, getTeamAndProductName, getUserFullName } from '../../helpers/utils'
import { CustomReferenceField } from '../Common/CustomReferenceFields'
import { OriginDestinationField, SimpleCalloutField } from '../Fields'
import CTList from '../List'

interface ColorScheme {
  color: string
  backgroundColor: string
}

type ColorSchemeType = Record<string, ColorScheme>

const COLOR_SCHEMES: ColorSchemeType = {
  [JobTrackingDashboardStatus.ON_TIME]: {
    color: kellyGreenD1,
    backgroundColor: success,
  },
  [JobTrackingDashboardStatus.WARNING]: {
    color: marigoldOrange,
    backgroundColor: paleyellow,
  },
  [JobTrackingDashboardStatus.NEEDS_ATTENTION]: {
    color: errorD1,
    backgroundColor: error,
  },
}

function getUserFacingTrackingStatus(tracking_status?: TrackingStatus) {
  if (tracking_status) {
    if (tracking_status === TrackingStatus.ERROR) {
      return 'No Data'
    }
    return capitalize(tracking_status.replace('_', ' '))
  }
  return 'Tracking Failure'
}

const Status = (props: any) => {
  const { record } = props
  const { color, backgroundColor } = COLOR_SCHEMES[record.job_tracking_dashboard_status]
  const dashboardStatus = getUserFacingTrackingStatus(record.tracking_status)
  return (
    <SimpleCalloutField
      styleProps={{
        style: {
          color: color,
          backgroundColor: backgroundColor,
          fontSize: 'small',
          borderRadius: '3px',
        },
      }}
      label={dashboardStatus}
      labelVariant="body2"
    />
  )
}

const DASHBOARD_STATUS_CHOICES = [
  { id: JobTrackingDashboardStatus.ON_TIME, name: 'On Time' },
  { id: JobTrackingDashboardStatus.WARNING, name: 'Warning' },
  { id: JobTrackingDashboardStatus.NEEDS_ATTENTION, name: 'Needs Attention' },
]

const JobTrackingFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <CheckboxGroupInput
      label="Progress"
      source="job_tracking_dashboard_status"
      choices={DASHBOARD_STATUS_CHOICES}
      alwaysOn
    />
  </Filter>
)
const rowStyle = () => ({
  height: '70px',
})

const handleRowClick = (id: string, redirect: any) => {
  const jobPath = `/jobs/${id}/show/route`
  redirect(jobPath)
  return ''
}

const JobTrackingDashboard = (props: any) => {
  const redirect = useRedirect()
  return (
    <CTList bulkActionButtons={false} filters={<JobTrackingFilter />} title="Job Tracking" {...props}>
      <Datagrid optimized rowClick={(id: any) => handleRowClick(id, redirect)} rowStyle={rowStyle}>
        <Status label="Progress Status" sortBy="tracking_ordering" />
        <TextField source="human_id" label="Job ID" sortable={false} />
        <TextField source="load.source_human_id" label="Broker Load ID" sortable={false} />
        <TextField source="load.broker.name" label="Broker" sortable={false} />
        <CustomReferenceField
          source="assigned_to"
          //@ts-ignore
          sortable={false}
          label="Assigned Driver"
          baseUrl="/users"
          representationFunction={getUserFullName}
        />
        <CustomReferenceField
          source="team"
          //@ts-ignore
          label="Team (Product)"
          sortable={false}
          baseUrl="/teams"
          representationFunction={getTeamAndProductName}
        />
        <FunctionField
          label={LABEL_ORIGIN_TO_DESTINATION}
          render={(record: any) => {
            return <OriginDestinationField record={record.load} />
          }}
        />
        <FunctionField
          label="Next Stop"
          render={(record: any) => {
            const currentLoadStopProgress = record?.tracking_current_load_stop_progress
            const numberOfStops = record?.load?.stops?.length
            const currentLoadStop = currentLoadStopProgress?.stop
            return (
              currentLoadStop && (
                <Box flexDirection="column">
                  <Typography variant="body2">
                    {`${currentLoadStop?.sequence_number}/${numberOfStops} Stops`}
                  </Typography>
                  <Typography variant="body2">
                    {`${currentLoadStop?.location_city}, ${currentLoadStop?.location_state}`}
                  </Typography>
                </Box>
              )
            )
          }}
        />
        <FunctionField
          label="Next Appointment Time"
          sortBy="tracking_current_load_stop_progress__stop__date_start"
          render={(record: any) => {
            const currentLoadStop = record?.tracking_current_load_stop_progress?.stop
            return currentLoadStop && getLocalizedStopAppointmentWindow(currentLoadStop)
          }}
        />
        <FunctionField
          label="ETA"
          render={(record: any) => {
            const currentLoadStopProgress = record?.tracking_current_load_stop_progress
            return (
              currentLoadStopProgress &&
              currentLoadStopProgress.date_estimated_arrival &&
              getFormattedDate(
                currentLoadStopProgress.date_estimated_arrival,
                currentLoadStopProgress.stop.location_timezone
              )
            )
          }}
        />
      </Datagrid>
    </CTList>
  )
}

export default JobTrackingDashboard
